import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import {
    extractViewBox,
    isValidSvg,
    getSvgDimensions,
    getGradientColor,
    paletteColorForPathIndex,
    collectPathPopulateGeometry,
    withMountedPathSvg,
} from "../../utils/svgUtils";
import {
    computePathPopulatePlacementsAsync,
    estimatePathLengthAsync,
    fingerprintSvgContent,
    hashPlacementToUnit,
    paletteColorIndexForPlacement,
    randomHexColorForPlacement,
    type PathPopulateProgress,
} from "../../utils/pathPopulatePlacement";
import {
    buildPathPopulateUnionSvgString,
    type UnionPlacementPayload,
} from "../../utils/pathPopulateUnionExport";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";

export type { PathPopulatePathSegment } from "../../utils/svgUtils";

export type PathPopulateGradientMode =
    | "per-path"
    | "global"
    | "random-per-path"
    | "random-per-instance"
    | "random-palette-per-instance"
    | "source-path-color";

interface UploadedSvg {
    name: string;
    content: string;
}

interface PathPopulateState {
    objectSvgs: UploadedSvg[];
    pathSvg: UploadedSvg | null;
    distance: number;
    scale: number;
    /** 0-100: scale varies +/-(value)% around base size. */
    scaleRandomness: number;
    /** Max arc-length jitter along path (px) per instance. */
    distanceOffsetRandomness: number;
    /** Max offset perpendicular to path tangent (px), uniform random per instance. */
    perpendicularScatterPx: number;
    error: string | null;
    rotationMode:
        | "align"
        | "random"
        | "random-90"
        | "none"
        | "polar"
        | "polar-inverted";
    baseRotationDeg: number;
    polarRotationCenterXPct: number;
    polarRotationCenterYPct: number;
    showPaths: boolean;
    gridEnabled: boolean;
    showGrid: boolean;
    gridSize: number;
    anchor: string;
    gradientEnabled: boolean;
    gradientMode: PathPopulateGradientMode;
    gradientColors: string[];
    sourcePathColors: string[];
    maxGradientSteps: number;
    gradientPerPathRepeats: number;
    objectPlacementMode: "alternating" | "random";
    widthProfileEnabled: boolean;
    widthProfile: WidthProfilePreset;
    widthProfileMode: "per-path" | "global";
    widthProfileMinScale: number;
    centerSizeFalloffEnabled: boolean;
    centerSizeFalloffEdgeScale: number;
    mergeUnionOnExport: boolean;
    setMergeUnionOnExport: (v: boolean) => void;
    setObjectSvgs: (svgs: UploadedSvg[]) => void;
    addObjectSvg: (svg: UploadedSvg) => void;
    removeObjectSvg: (index: number) => void;
    setPathSvg: (svg: UploadedSvg | null) => void;
    setObjectPlacementMode: (mode: "alternating" | "random") => void;
    setDistance: (d: number) => void;
    setScale: (s: number) => void;
    setScaleRandomness: (r: number) => void;
    setDistanceOffsetRandomness: (r: number) => void;
    setPerpendicularScatterPx: (r: number) => void;
    setError: (e: string | null) => void;
    setRotationMode: (
        mode:
            | "align"
            | "random"
            | "random-90"
            | "none"
            | "polar"
            | "polar-inverted",
    ) => void;
    setBaseRotationDeg: (r: number) => void;
    setPolarRotationCenterXPct: (v: number) => void;
    setPolarRotationCenterYPct: (v: number) => void;
    setShowPaths: (show: boolean) => void;
    setGridEnabled: (enabled: boolean) => void;
    setShowGrid: (show: boolean) => void;
    setGridSize: (size: number) => void;
    setAnchor: (anchor: string) => void;
    setGradientEnabled: (enabled: boolean) => void;
    setGradientMode: (mode: PathPopulateGradientMode) => void;
    setGradientColors: (colors: string[]) => void;
    setMaxGradientSteps: (steps: number) => void;
    setGradientPerPathRepeats: (repeats: number) => void;
    setWidthProfileEnabled: (enabled: boolean) => void;
    setWidthProfile: (profile: WidthProfilePreset) => void;
    setWidthProfileMode: (mode: "per-path" | "global") => void;
    setWidthProfileMinScale: (minScale: number) => void;
    setCenterSizeFalloffEnabled: (enabled: boolean) => void;
    setCenterSizeFalloffEdgeScale: (edgeScale: number) => void;
    objectInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
    pathInputRef: React.RefObject<HTMLInputElement>;
    handleFileLoad: (
        kind: "object" | "path",
        objectIndex?: number,
    ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    pathSegmentCount: number;
    /** Sum of getTotalLength() for path geometry (SVG user units); null if no path. */
    estimatedPathLengthPx: number | null;
    pathViewBox: string | null;
    placements: {
        x: number;
        y: number;
        angle: number;
        pathIndex: number;
        pathPosition: number;
        globalPosition: number;
        objectIndex: number;
    }[];
    /**
     * Approximate instance count for the current path + distance + grid settings.
     * Computed cheaply from `totalLength / distance` - does not run sampling.
     */
    estimatedPlacementCount: number;
    /** True when the viewer is showing instances for the current path/object/spacing settings. */
    hasGeneratedPlacementsForCurrentParams: boolean;
    generatePlacements: () => void;
    /** Cheap path length + segment count measurement still running. */
    pathLengthComputePending: boolean;
    /** Full placement sampling/dedup still running (only during/after Generate). */
    placementComputePending: boolean;
    /** Progress from the active placement compute, or `null` when idle. */
    placementComputeProgress: PathPopulateProgress | null;
    /** Cancel an in-flight placement compute (returns the UI to the estimate state). */
    cancelPlacementCompute: () => void;
    objectInnerSvgs: Array<{
        content: string;
        viewBox: string;
        dimensions: { width: number; height: number } | null;
    }>;
    scaleFactor: number;
    viewBoxAttr: string;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const PathPopulateContext = createContext<PathPopulateState | null>(null);

export function usePathPopulate() {
    const context = useContext(PathPopulateContext);
    if (!context) {
        throw new Error(
            "usePathPopulate must be used within PathPopulateProvider",
        );
    }
    return context;
}

interface PathPopulateProviderProps {
    children: React.ReactNode;
    initialDistance?: number;
    initialScale?: number;
}

// Helper function to convert anchor string to x, y coordinates (0-1)
export function getAnchorCoordinates(anchor: string): { x: number; y: number } {
    const anchorMap: Record<string, { x: number; y: number }> = {
        "top-left": { x: 0, y: 0 },
        top: { x: 0.5, y: 0 },
        "top-right": { x: 1, y: 0 },
        "center-left": { x: 0, y: 0.5 },
        center: { x: 0.5, y: 0.5 },
        "center-right": { x: 1, y: 0.5 },
        "bottom-left": { x: 0, y: 1 },
        bottom: { x: 0.5, y: 1 },
        "bottom-right": { x: 1, y: 1 },
    };
    return anchorMap[anchor] || anchorMap["center"];
}

export type WidthProfilePreset =
    | "uniform"
    | "taper-both"
    | "taper-start"
    | "taper-end"
    | "bulge-center"
    | "s-curve-ramp";

export const WIDTH_PROFILE_LABELS: Record<WidthProfilePreset, string> = {
    uniform: "Uniform",
    "taper-both": "Taper both ends",
    "taper-start": "Taper start",
    "taper-end": "Taper end",
    "bulge-center": "Bulge center",
    "s-curve-ramp": "S-curve ramp",
};

const DEFAULT_PATH_SVG_CONTENT = `<?xml version="1.0" encoding="UTF-8"?>
<svg id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 461.1 461.13">
  <g id="Layer_1-2" data-name="Layer 1">
    <g>
      <path d="m74.78,1.13c67.14,0,164.45,53.93,247.91,137.4,56.24,56.24,99.48,118.74,121.74,175.99,10.8,27.77,16.06,52.78,15.64,74.32-.44,22.17-6.92,39.67-19.26,52.01-12.79,12.79-31.1,19.28-54.4,19.28-67.14,0-164.45-53.93-247.92-137.4-56.24-56.24-99.48-118.74-121.74-175.99C5.96,118.97.69,93.97,1.12,72.42c.44-22.17,6.92-39.67,19.26-52.01C33.17,7.62,51.47,1.13,74.78,1.13h0m0-1c-22.85,0-41.83,6.29-55.11,19.57-51.26,51.26,1.63,187.24,118.12,303.73,86.3,86.3,183.3,137.69,248.62,137.69,22.85,0,41.83-6.29,55.11-19.57,51.26-51.26-1.63-187.24-118.12-303.73C237.1,51.52,140.1.13,74.78.13h0Z"/>
      <path d="m386.33,1c23.3,0,41.6,6.49,54.39,19.28,12.34,12.34,18.82,29.84,19.26,52.01.43,21.54-4.84,46.55-15.64,74.32-22.26,57.24-65.5,119.74-121.74,175.99-83.47,83.47-180.78,137.4-247.92,137.4-23.31,0-41.61-6.49-54.4-19.28-50.78-50.78,2.2-186.4,118.12-302.32C221.86,54.94,319.17,1.01,386.33,1h0M386.32,0c-65.32,0-162.33,51.4-248.62,137.69C21.2,254.18-31.68,390.17,19.57,441.43c13.29,13.29,32.25,19.57,55.11,19.57,65.32,0,162.33-51.4,248.62-137.69,116.49-116.49,169.38-252.48,118.12-303.73C428.14,6.29,409.17,0,386.32,0h0Z"/>
    </g>
  </g>
</svg>`;
const FIRST_USE_AUTOGEN_KEY = "object-along-path:first-use-autogen:v1";

const profileFunctions: Record<WidthProfilePreset, (t: number) => number> = {
    uniform: () => 1,
    "taper-both": (t) => Math.sin(Math.PI * t),
    "taper-start": (t) => t,
    "taper-end": (t) => 1 - t,
    "bulge-center": (t) => Math.sin(Math.PI * t) ** 2,
    "s-curve-ramp": (t) => 3 * t * t - 2 * t * t * t,
};

export function getWidthProfileScale(
    position: number,
    profile: WidthProfilePreset,
    minScale: number,
): number {
    const fn = profileFunctions[profile] ?? profileFunctions.uniform;
    const raw = fn(Math.max(0, Math.min(1, position)));
    return minScale + (1 - minScale) * raw;
}

function parseViewBoxRect(
    viewBox: string | null,
): { x: number; y: number; width: number; height: number } | null {
    if (!viewBox) return null;
    const parts = viewBox.split(/\s+/).map(Number);
    if (
        parts.length !== 4 ||
        !parts.every((v) => Number.isFinite(v)) ||
        parts[2] <= 0 ||
        parts[3] <= 0
    ) {
        return null;
    }
    const [x, y, width, height] = parts;
    return { x, y, width, height };
}

export function getCenterSizeFalloffMultiplier(
    p: { x: number; y: number },
    viewBox: string | null,
    edgeScale: number,
): number {
    const vb = parseViewBoxRect(viewBox);
    if (!vb) return 1;
    const clampedEdge = Math.max(0, Math.min(1, edgeScale));
    const cx = vb.x + vb.width / 2;
    const cy = vb.y + vb.height / 2;
    const rx = vb.width / 2;
    const ry = vb.height / 2;
    if (rx <= 0 || ry <= 0) return 1;
    const nx = (p.x - cx) / rx;
    const ny = (p.y - cy) / ry;
    const dist = Math.min(1, Math.sqrt(nx * nx + ny * ny));
    return 1 - (1 - clampedEdge) * dist;
}

/** Seeded multiplier in [1 − r, 1 + r] with r = scaleRandomness / 100. */
export function getPlacementScaleRandomMultiplier(
    p: { x: number; y: number; pathIndex: number },
    idx: number,
    scaleRandomness: number,
): number {
    if (scaleRandomness <= 0) return 1;
    const u = hashPlacementToUnit(p.pathIndex, idx, 0x5c8d9e3f);
    return 1 + (u * 2 - 1) * (scaleRandomness / 100);
}

/** Seeded offset along the left normal to the path tangent (SVG coords). */
export function getPlacementPerpendicularScatterOffset(
    p: { pathIndex: number; angle: number },
    idx: number,
    maxPx: number,
): { dx: number; dy: number } {
    if (maxPx <= 0) return { dx: 0, dy: 0 };
    const u = hashPlacementToUnit(p.pathIndex, idx, 0x91e5d7c3);
    const r = (u * 2 - 1) * maxPx;
    const nx = -Math.sin(p.angle);
    const ny = Math.cos(p.angle);
    return { dx: r * nx, dy: r * ny };
}

interface PlacementRotationInput {
    rotationMode:
        | "align"
        | "random"
        | "random-90"
        | "none"
        | "polar"
        | "polar-inverted";
    baseRotationDeg: number;
    placement: { x: number; y: number; angle: number; pathIndex: number };
    placementIndex: number;
    polarCenter?: { x: number; y: number } | null;
    objectDimensions?: { width: number; height: number } | null;
    placementScale?: number;
    anchor?: string;
    scatter?: { dx: number; dy: number };
}

export function getPlacementRotationDegrees(
    input: PlacementRotationInput,
): number {
    const {
        rotationMode,
        baseRotationDeg,
        placement: p,
        placementIndex,
        polarCenter,
        objectDimensions,
        placementScale = 1,
        anchor = "center",
        scatter = { dx: 0, dy: 0 },
    } = input;
    if (rotationMode === "align") {
        return (p.angle * 180) / Math.PI + baseRotationDeg;
    }
    if (rotationMode === "random") {
        const u = hashPlacementToUnit(p.pathIndex, placementIndex, 0x6c8e9cf1);
        return u * 360 + baseRotationDeg;
    }
    if (rotationMode === "random-90") {
        const u = hashPlacementToUnit(p.pathIndex, placementIndex, 0x1d4a7bf3);
        const quarterTurns = Math.floor(u * 4);
        return quarterTurns * 90 + baseRotationDeg;
    }
    if (
        (rotationMode === "polar" || rotationMode === "polar-inverted") &&
        polarCenter &&
        objectDimensions
    ) {
        const anchorCoords = getAnchorCoordinates(anchor);
        const offsetX =
            -anchorCoords.x * objectDimensions.width * placementScale;
        const offsetY =
            -anchorCoords.y * objectDimensions.height * placementScale;
        const centerX =
            p.x +
            offsetX +
            scatter.dx +
            (objectDimensions.width * placementScale) / 2;
        const centerY =
            p.y +
            offsetY +
            scatter.dy +
            (objectDimensions.height * placementScale) / 2;
        const dx = centerX - polarCenter.x;
        const dy = centerY - polarCenter.y;
        const angleRad =
            rotationMode === "polar"
                ? Math.atan2(dx, dy) // 0 at bottom, pi at top
                : Math.atan2(dx, -dy); // 0 at top, pi at bottom
        return (angleRad * 180) / Math.PI + baseRotationDeg;
    }
    return baseRotationDeg;
}

export function getRepeatedGradientPosition(
    position: number,
    repeats: number,
): number {
    const p = Math.max(0, Math.min(1, position));
    const r = Math.max(1, Math.floor(repeats));
    if (r === 1) return p;
    if (p >= 1) return 1;
    const repeated = (p * r) % 1;
    return repeated < 0 ? repeated + 1 : repeated;
}

// Helper function to apply fill color to SVG content
function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";

    // Use regex to find and replace fill attributes in all SVG elements
    // This will override existing fills and add fills to elements that don't have them
    // Match common SVG elements: path, circle, rect, ellipse, polygon, polyline, line
    // Handle both self-closing and regular tags
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;

    return svgContent.replace(
        elementRegex,
        (match, tagName, attributes, selfClose) => {
            // Remove existing fill attribute if present
            const cleanedAttributes = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );

            // Add new fill attribute
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

function pickColorFromValue(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === "none") return null;
    return trimmed;
}

function resolvePathSegmentColor(el: SVGGeometryElement): string | null {
    const fill = pickColorFromValue(el.getAttribute("fill"));
    if (fill) return fill;
    const stroke = pickColorFromValue(el.getAttribute("stroke"));
    if (stroke) return stroke;

    if (typeof window === "undefined") return null;
    const computed = window.getComputedStyle(el);
    const computedFill = pickColorFromValue(computed.fill);
    if (computedFill) return computedFill;
    const computedStroke = pickColorFromValue(computed.stroke);
    if (computedStroke) return computedStroke;
    return null;
}

export function PathPopulateProvider({
    children,
    initialDistance = 10,
    initialScale = 50,
}: PathPopulateProviderProps) {
    const [objectSvgs, setObjectSvgs] = useState<UploadedSvg[]>(() => {
        const squarePreset = OBJECT_SVG_PRESETS.find((p) => p.id === "square");
        if (!squarePreset || !isValidSvg(squarePreset.svg)) return [];
        return [
            { name: `${squarePreset.label} (preset)`, content: squarePreset.svg },
        ];
    });
    const [pathSvg, setPathSvg] = useState<UploadedSvg | null>(null);
    const [objectPlacementMode, setObjectPlacementMode] = useState<
        "alternating" | "random"
    >("alternating");
    const [distance, setDistance] = useState(initialDistance);
    const [scale, setScale] = useState(initialScale);
    const [scaleRandomness, setScaleRandomness] = useState(0);
    const [distanceOffsetRandomness, setDistanceOffsetRandomness] = useState(0);
    const [perpendicularScatterPx, setPerpendicularScatterPx] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [rotationMode, setRotationMode] = useState<
        "align" | "random" | "random-90" | "none" | "polar" | "polar-inverted"
    >("align");
    const [baseRotationDeg, setBaseRotationDeg] = useState(0);
    const [polarRotationCenterXPct, setPolarRotationCenterXPct] = useState(50);
    const [polarRotationCenterYPct, setPolarRotationCenterYPct] = useState(50);
    const [showPaths, setShowPaths] = useState(true);
    const [gridEnabled, setGridEnabled] = useState(false);
    const [showGrid, setShowGrid] = useState(false);
    const [gridSize, setGridSize] = useState(10);
    const [anchor, setAnchor] = useState("center");
    const [gradientEnabled, setGradientEnabled] = useState(true);
    const [gradientMode, setGradientMode] =
        useState<PathPopulateGradientMode>("random-palette-per-instance");
    const [gradientColors, setGradientColors] = useState<string[]>([
        "#285cd7",
        "#ff8fea",
        "#ff5252",
        "#000000",
        "#52e075",
    ]);
    const [maxGradientSteps, setMaxGradientSteps] = useState(10);
    const [gradientPerPathRepeats, setGradientPerPathRepeats] = useState(1);
    const [widthProfileEnabled, setWidthProfileEnabled] = useState(false);
    const [widthProfile, setWidthProfile] =
        useState<WidthProfilePreset>("taper-both");
    const [widthProfileMode, setWidthProfileMode] = useState<
        "per-path" | "global"
    >("per-path");
    const [widthProfileMinScale, setWidthProfileMinScale] = useState(0.1);
    const [centerSizeFalloffEnabled, setCenterSizeFalloffEnabled] =
        useState(false);
    const [centerSizeFalloffEdgeScale, setCenterSizeFalloffEdgeScale] =
        useState(0.5);
    const [mergeUnionOnExport, setMergeUnionOnExport] = useState(false);
    const defaultPathLoadedRef = useRef(false);
    const firstUseAutogenConsumedRef = useRef(false);
    const [shouldAutogenOnFirstUse, setShouldAutogenOnFirstUse] = useState(
        () => {
            if (typeof window === "undefined") return false;
            try {
                return localStorage.getItem(FIRST_USE_AUTOGEN_KEY) !== "1";
            } catch {
                return false;
            }
        },
    );

    const objectInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const pathInputRef = useRef<HTMLInputElement | null>(null);

    const addObjectSvg = (svg: UploadedSvg) => {
        setObjectSvgs([...objectSvgs, svg]);
    };

    const removeObjectSvg = (index: number) => {
        const newSvgs = objectSvgs.filter((_, i) => i !== index);
        setObjectSvgs(newSvgs);
    };

    const handleFileLoad =
        (kind: "object" | "path", objectIndex?: number) =>
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            const file = event.target.files?.[0];
            if (!file) return;

            setError(null);

            if (
                !file.type.includes("svg") &&
                !file.name.toLowerCase().endsWith(".svg")
            ) {
                setError("Please choose an SVG file.");
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";

                if (!text) {
                    setError("Unable to read SVG file.");
                    return;
                }

                if (!isValidSvg(text)) {
                    setError("Selected file does not appear to be valid SVG.");
                    return;
                }

                const payload: UploadedSvg = {
                    name: file.name,
                    content: text,
                };

                if (kind === "object") {
                    if (objectIndex !== undefined) {
                        // Replace existing object at index
                        const newSvgs = [...objectSvgs];
                        newSvgs[objectIndex] = payload;
                        setObjectSvgs(newSvgs);
                    } else {
                        // Add new object
                        addObjectSvg(payload);
                    }
                } else {
                    setPathSvg(payload);
                }
            };

            reader.onerror = () => {
                setError("Error reading file.");
            };

            reader.readAsText(file);
        };

    const pathViewBox = useMemo(() => {
        if (!pathSvg) return null;
        return extractViewBox(pathSvg.content)?.viewBox ?? null;
    }, [pathSvg]);

    useEffect(() => {
        if (defaultPathLoadedRef.current || pathSvg) return;
        defaultPathLoadedRef.current = true;
        if (!isValidSvg(DEFAULT_PATH_SVG_CONTENT)) return;
        setPathSvg((prev) => {
            if (prev) return prev;
            return { name: "default-image.svg", content: DEFAULT_PATH_SVG_CONTENT };
        });
    }, [pathSvg]);

    const sourcePathColors = useMemo(() => {
        if (!pathSvg) return [];
        try {
            const mounted = withMountedPathSvg(pathSvg.content, (root) => {
                const segments = collectPathPopulateGeometry(root);
                return segments.map(({ el }) => resolvePathSegmentColor(el));
            });
            if (!mounted) return [];
            return mounted.map((color) => color ?? "");
        } catch {
            return [];
        }
    }, [pathSvg]);

    const polarRotationCenter = useMemo(() => {
        const vb = parseViewBoxRect(pathViewBox);
        if (!vb) return null;
        return {
            x: vb.x + (vb.width * polarRotationCenterXPct) / 100,
            y: vb.y + (vb.height * polarRotationCenterYPct) / 100,
        };
    }, [pathViewBox, polarRotationCenterXPct, polarRotationCenterYPct]);

    const pathContentKey = pathSvg
        ? fingerprintSvgContent(pathSvg.content)
        : "";

    const placementDepsKey = useMemo(() => {
        if (!pathSvg || objectSvgs.length === 0) return "";
        return [
            pathContentKey,
            objectSvgs.map((o) => fingerprintSvgContent(o.content)).join(","),
            distance,
            distanceOffsetRandomness,
            gridEnabled ? 1 : 0,
            gridSize,
            objectPlacementMode,
        ].join("|");
    }, [
        pathSvg,
        pathContentKey,
        objectSvgs,
        distance,
        distanceOffsetRandomness,
        gridEnabled,
        gridSize,
        objectPlacementMode,
    ]);

    const pathLengthJobGen = useRef(0);
    const placementJobGen = useRef(0);
    const placementAbortRef = useRef<AbortController | null>(null);

    const [pathLengthPayload, setPathLengthPayload] = useState<{
        pathKey: string;
        px: number | null;
        segmentCount: number;
    } | null>(null);

    const [placementAsyncPayload, setPlacementAsyncPayload] = useState<{
        key: string;
        pathSegmentCount: number;
        placements: PathPopulateState["placements"];
    } | null>(null);

    const [pathLengthComputePending, setPathLengthComputePending] =
        useState(false);
    const [placementComputePending, setPlacementComputePending] =
        useState(false);
    const [placementComputeProgress, setPlacementComputeProgress] =
        useState<PathPopulateProgress | null>(null);

    const [placementGeneratedKey, setPlacementGeneratedKey] = useState<
        string | null
    >(null);

    // Debounce path-length estimate so rapid path-SVG swaps / pastes don't
    // thrash the mount+measure pipeline. 120ms is small enough to feel instant.
    useEffect(() => {
        if (!pathSvg) {
            setPathLengthPayload(null);
            setPathLengthComputePending(false);
            return;
        }
        const pathKey = pathContentKey;
        const ac = new AbortController();
        const myGen = ++pathLengthJobGen.current;
        setPathLengthComputePending(true);
        const debounceId = setTimeout(() => {
            (async () => {
                try {
                    const res = await estimatePathLengthAsync(
                        pathSvg.content,
                        ac.signal,
                    );
                    if (pathLengthJobGen.current !== myGen) return;
                    setPathLengthPayload({
                        pathKey,
                        px: res?.totalLengthPx ?? null,
                        segmentCount: res?.segmentCount ?? 0,
                    });
                } catch (e) {
                    if (e instanceof DOMException && e.name === "AbortError") {
                        return;
                    }
                    console.warn("Path length estimation failed:", e);
                    if (pathLengthJobGen.current !== myGen) return;
                    setPathLengthPayload({
                        pathKey,
                        px: null,
                        segmentCount: 0,
                    });
                } finally {
                    if (pathLengthJobGen.current === myGen) {
                        setPathLengthComputePending(false);
                    }
                }
            })();
        }, 120);
        return () => {
            clearTimeout(debounceId);
            ac.abort();
        };
    }, [pathSvg, pathContentKey]);

    // Invalidate the last "Generate" result when anything affecting placement changes.
    useEffect(() => {
        setPlacementGeneratedKey(null);
        placementAbortRef.current?.abort();
        placementAbortRef.current = null;
        setPlacementComputeProgress(null);
    }, [placementDepsKey]);

    // Heavy placement compute only runs when the user has pressed Generate for
    // the current params (placementGeneratedKey === placementDepsKey).
    useEffect(() => {
        if (
            !pathSvg ||
            distance <= 0 ||
            placementDepsKey === "" ||
            placementGeneratedKey !== placementDepsKey
        ) {
            return;
        }
        // Skip if we already have a result for this key.
        if (
            placementAsyncPayload &&
            placementAsyncPayload.key === placementDepsKey
        ) {
            return;
        }
        const key = placementDepsKey;
        const ac = new AbortController();
        placementAbortRef.current = ac;
        const myGen = ++placementJobGen.current;
        setPlacementComputePending(true);
        setPlacementComputeProgress({
            phase: "mount",
            processed: 0,
            total: 1,
        });
        (async () => {
            try {
                const r = await computePathPopulatePlacementsAsync({
                    pathSvgContent: pathSvg.content,
                    distance,
                    distanceOffsetRandomness,
                    gridEnabled,
                    gridSize,
                    objectCount: objectSvgs.length,
                    objectPlacementMode,
                    signal: ac.signal,
                    onProgress: (info) => {
                        if (placementJobGen.current !== myGen) return;
                        setPlacementComputeProgress(info);
                    },
                });
                if (placementJobGen.current !== myGen) return;
                setPlacementAsyncPayload({
                    key,
                    pathSegmentCount: r.pathSegmentCount,
                    placements: r.placements,
                });
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                }
                console.warn("Placement compute failed:", e);
                if (placementJobGen.current !== myGen) return;
                setPlacementAsyncPayload({
                    key,
                    pathSegmentCount: 0,
                    placements: [],
                });
            } finally {
                if (placementJobGen.current === myGen) {
                    setPlacementComputePending(false);
                    setPlacementComputeProgress(null);
                }
            }
        })();
        return () => {
            ac.abort();
        };
    }, [
        placementDepsKey,
        placementGeneratedKey,
        pathSvg,
        distance,
        distanceOffsetRandomness,
        gridEnabled,
        gridSize,
        objectSvgs,
        objectPlacementMode,
        placementAsyncPayload,
    ]);

    const resolvedPlacements =
        placementAsyncPayload && placementAsyncPayload.key === placementDepsKey
            ? placementAsyncPayload.placements
            : [];

    const estimatedPathLengthPx =
        pathLengthPayload && pathLengthPayload.pathKey === pathContentKey
            ? pathLengthPayload.px
            : null;

    const pathSegmentCount =
        placementAsyncPayload && placementAsyncPayload.key === placementDepsKey
            ? placementAsyncPayload.pathSegmentCount
            : pathLengthPayload && pathLengthPayload.pathKey === pathContentKey
              ? pathLengthPayload.segmentCount
              : 0;

    const hasGeneratedPlacementsForCurrentParams =
        placementGeneratedKey != null &&
        placementDepsKey !== "" &&
        placementGeneratedKey === placementDepsKey &&
        placementAsyncPayload?.key === placementDepsKey;

    const placements = hasGeneratedPlacementsForCurrentParams
        ? resolvedPlacements
        : [];

    // Cheap pre-Generate estimate: `totalLength / distance`, capped to something
    // sane. Not exact (dedup + grid snapping can reduce the real count) but
    // fast and lets the user decide before paying for full sampling.
    const estimatedPlacementCount = useMemo(() => {
        if (placementDepsKey === "") return 0;
        if (hasGeneratedPlacementsForCurrentParams) {
            return resolvedPlacements.length;
        }
        if (
            estimatedPathLengthPx == null ||
            estimatedPathLengthPx <= 0 ||
            distance <= 0
        ) {
            return 0;
        }
        return Math.max(1, Math.floor(estimatedPathLengthPx / distance));
    }, [
        placementDepsKey,
        hasGeneratedPlacementsForCurrentParams,
        resolvedPlacements.length,
        estimatedPathLengthPx,
        distance,
    ]);

    const generatePlacements = useCallback(() => {
        if (placementDepsKey === "") return;
        setPlacementGeneratedKey(placementDepsKey);
    }, [placementDepsKey]);

    const cancelPlacementCompute = useCallback(() => {
        placementAbortRef.current?.abort();
        placementAbortRef.current = null;
        setPlacementGeneratedKey(null);
        setPlacementComputeProgress(null);
    }, []);

    useEffect(() => {
        if (!shouldAutogenOnFirstUse || firstUseAutogenConsumedRef.current) {
            return;
        }
        if (placementDepsKey === "") return;
        firstUseAutogenConsumedRef.current = true;
        setPlacementGeneratedKey(placementDepsKey);
        setShouldAutogenOnFirstUse(false);
        try {
            localStorage.setItem(FIRST_USE_AUTOGEN_KEY, "1");
        } catch {
            /* ignore storage failures */
        }
    }, [shouldAutogenOnFirstUse, placementDepsKey]);

    const objectInnerSvgs = useMemo(() => {
        return objectSvgs.map((svg) => {
            const innerMatch = svg.content.match(
                /<svg[^>]*>([\s\S]*?)<\/svg>/i,
            );
            const innerContent = innerMatch ? innerMatch[1] : "";
            const viewBox =
                extractViewBox(svg.content)?.viewBox ?? "0 0 100 100";
            const dimensions = getSvgDimensions(svg.content);
            return {
                content: innerContent,
                viewBox,
                dimensions,
            };
        });
    }, [objectSvgs]);

    // Calculate scale factor based on matching the larger dimension to the size parameter
    // Use first object's dimensions as reference (or default scale if no objects)
    const scaleFactor = useMemo(() => {
        if (objectInnerSvgs.length === 0 || scale <= 0) return scale;
        const firstDimensions = objectInnerSvgs[0]?.dimensions;
        if (!firstDimensions) return scale;
        const maxDimension = Math.max(
            firstDimensions.width,
            firstDimensions.height,
        );
        return scale / maxDimension;
    }, [objectInnerSvgs, scale]);

    // Calculate expanded viewBox that includes all placed objects
    const viewBoxAttr = useMemo(() => {
        const baseViewBox = pathViewBox ?? "0 0 200 200";
        const viewBoxParts = baseViewBox.split(/\s+/).map(Number);
        if (viewBoxParts.length !== 4) return baseViewBox;

        let [minX, minY, baseWidth, baseHeight] = viewBoxParts;
        let maxX = minX + baseWidth;
        let maxY = minY + baseHeight;

        // Expand bounds to include all placed objects
        if (placements.length > 0 && objectInnerSvgs.length > 0) {
            for (let idx = 0; idx < placements.length; idx++) {
                const p = placements[idx];
                const objectSvg = objectInnerSvgs[p.objectIndex];
                if (!objectSvg || !objectSvg.dimensions) continue;

                // Compute per-placement scale
                const wpPosition =
                    widthProfileMode === "per-path"
                        ? p.pathPosition
                        : p.globalPosition;
                const placementScale =
                    (widthProfileEnabled
                        ? scaleFactor *
                          getWidthProfileScale(
                              wpPosition,
                              widthProfile,
                              widthProfileMinScale,
                          )
                        : scaleFactor) *
                    (centerSizeFalloffEnabled
                        ? getCenterSizeFalloffMultiplier(
                              p,
                              pathViewBox,
                              centerSizeFalloffEdgeScale,
                          )
                        : 1) *
                    getPlacementScaleRandomMultiplier(p, idx, scaleRandomness);

                // Calculate anchor offset
                const anchorCoords = getAnchorCoordinates(anchor);
                const offsetX =
                    -anchorCoords.x *
                    objectSvg.dimensions.width *
                    placementScale;
                const offsetY =
                    -anchorCoords.y *
                    objectSvg.dimensions.height *
                    placementScale;

                const scatter = getPlacementPerpendicularScatterOffset(
                    p,
                    idx,
                    perpendicularScatterPx,
                );
                const angleDeg = getPlacementRotationDegrees({
                    rotationMode,
                    baseRotationDeg,
                    placement: p,
                    placementIndex: idx,
                    polarCenter: polarRotationCenter,
                    objectDimensions: objectSvg.dimensions,
                    placementScale,
                    anchor,
                    scatter,
                });
                const angleRad = (angleDeg * Math.PI) / 180;

                // Object bounds in local coordinate system (already scaled)
                const objWidth = objectSvg.dimensions.width * placementScale;
                const objHeight = objectSvg.dimensions.height * placementScale;

                // The transform is: translate(p.x + offsetX, p.y + offsetY) rotate(angleDeg, -offsetX, -offsetY) scale(scaleFactor)
                // Since scale is applied last in SVG, but we've already scaled the dimensions,
                // we need to work backwards. The actual transform order (right-to-left in SVG):
                // 1. scale(scaleFactor) - already in objWidth/objHeight
                // 2. rotate(angleDeg, -offsetX, -offsetY) - rotate around anchor in local space
                // 3. translate(p.x + offsetX, p.y + offsetY) - move to final position

                // Local corners of the scaled object
                const localCorners = [
                    { x: 0, y: 0 },
                    { x: objWidth, y: 0 },
                    { x: objWidth, y: objHeight },
                    { x: 0, y: objHeight },
                ];

                const cos = Math.cos(angleRad);
                const sin = Math.sin(angleRad);

                // Transform each corner
                for (const corner of localCorners) {
                    // Rotate around anchor point (-offsetX, -offsetY) in local space
                    const dx = corner.x - -offsetX;
                    const dy = corner.y - -offsetY;
                    const rotatedX = dx * cos - dy * sin + -offsetX;
                    const rotatedY = dx * sin + dy * cos + -offsetY;

                    // Translate to final position (include perpendicular scatter)
                    const worldX = rotatedX + p.x + offsetX + scatter.dx;
                    const worldY = rotatedY + p.y + offsetY + scatter.dy;

                    minX = Math.min(minX, worldX);
                    minY = Math.min(minY, worldY);
                    maxX = Math.max(maxX, worldX);
                    maxY = Math.max(maxY, worldY);
                }
            }
        }

        // Add some padding (5% of dimensions)
        const paddingX = (maxX - minX) * 0.05;
        const paddingY = (maxY - minY) * 0.05;
        minX -= paddingX;
        minY -= paddingY;
        maxX += paddingX;
        maxY += paddingY;

        const width = maxX - minX;
        const height = maxY - minY;

        return `${minX} ${minY} ${width} ${height}`;
    }, [
        pathViewBox,
        placements,
        objectInnerSvgs,
        rotationMode,
        baseRotationDeg,
        anchor,
        scaleFactor,
        scaleRandomness,
        widthProfileEnabled,
        widthProfile,
        widthProfileMode,
        widthProfileMinScale,
        centerSizeFalloffEnabled,
        centerSizeFalloffEdgeScale,
        perpendicularScatterPx,
    ]);

    const canExport =
        !!pathSvg &&
        objectInnerSvgs.length > 0 &&
        placements.length > 0 &&
        !!viewBoxAttr;

    const buildExportSvgString = () => {
        if (!pathSvg || objectInnerSvgs.length === 0) return "";

        if (mergeUnionOnExport && placements.length > 0) {
            const placementPayloads: UnionPlacementPayload[] = [];
            for (let idx = 0; idx < placements.length; idx++) {
                const p = placements[idx];
                const objectSvg = objectInnerSvgs[p.objectIndex];
                if (!objectSvg) continue;

                const wpPosition =
                    widthProfileMode === "per-path"
                        ? p.pathPosition
                        : p.globalPosition;
                const placementScale =
                    (widthProfileEnabled
                        ? scaleFactor *
                          getWidthProfileScale(
                              wpPosition,
                              widthProfile,
                              widthProfileMinScale,
                          )
                        : scaleFactor) *
                    (centerSizeFalloffEnabled
                        ? getCenterSizeFalloffMultiplier(
                              p,
                              pathViewBox,
                              centerSizeFalloffEdgeScale,
                          )
                        : 1) *
                    getPlacementScaleRandomMultiplier(p, idx, scaleRandomness);

                const anchorCoords = getAnchorCoordinates(anchor);
                let offsetX = 0;
                let offsetY = 0;
                if (objectSvg.dimensions) {
                    offsetX =
                        -anchorCoords.x *
                        objectSvg.dimensions.width *
                        placementScale;
                    offsetY =
                        -anchorCoords.y *
                        objectSvg.dimensions.height *
                        placementScale;
                }

                const scatter = getPlacementPerpendicularScatterOffset(
                    p,
                    idx,
                    perpendicularScatterPx,
                );
                const angleDeg = getPlacementRotationDegrees({
                    rotationMode,
                    baseRotationDeg,
                    placement: p,
                    placementIndex: idx,
                    polarCenter: polarRotationCenter,
                    objectDimensions: objectSvg.dimensions,
                    placementScale,
                    anchor,
                    scatter,
                });

                const transform = `translate(${p.x + offsetX + scatter.dx}, ${p.y + offsetY + scatter.dy}) rotate(${angleDeg}, ${-offsetX}, ${-offsetY}) scale(${placementScale})`;
                placementPayloads.push({
                    transform,
                    objectContent: objectSvg.content,
                    viewBox: objectSvg.viewBox,
                });
            }

            const unionSvg = buildPathPopulateUnionSvgString({
                pathSvgFullContent: pathSvg.content,
                showPaths,
                viewBoxAttr,
                placements: placementPayloads,
            });
            if (unionSvg) return unionSvg;
        }

        // Remove XML declaration and SVG tags from path content
        const pathContent = showPaths
            ? pathSvg.content
                  .replace(/<\?xml[^>]*\?>/gi, "")
                  .replace(/<\/?svg[^>]*>/g, "")
                  .trim()
            : "";

        // Generate elements with transforms - always render SVG content directly
        const objectElements = placements
            .map((p, idx) => {
                // Get the object SVG for this placement
                const objectSvg = objectInnerSvgs[p.objectIndex];
                if (!objectSvg) return "";

                let svgContent = objectSvg.content;

                // Compute per-placement scale
                const wpPosition =
                    widthProfileMode === "per-path"
                        ? p.pathPosition
                        : p.globalPosition;
                const placementScale =
                    (widthProfileEnabled
                        ? scaleFactor *
                          getWidthProfileScale(
                              wpPosition,
                              widthProfile,
                              widthProfileMinScale,
                          )
                        : scaleFactor) *
                    (centerSizeFalloffEnabled
                        ? getCenterSizeFalloffMultiplier(
                              p,
                              pathViewBox,
                              centerSizeFalloffEdgeScale,
                          )
                        : 1) *
                    getPlacementScaleRandomMultiplier(p, idx, scaleRandomness);

                // Calculate anchor offset based on this object's dimensions
                const anchorCoords = getAnchorCoordinates(anchor);
                let offsetX = 0;
                let offsetY = 0;
                if (objectSvg.dimensions) {
                    offsetX =
                        -anchorCoords.x *
                        objectSvg.dimensions.width *
                        placementScale;
                    offsetY =
                        -anchorCoords.y *
                        objectSvg.dimensions.height *
                        placementScale;
                }

                const scatter = getPlacementPerpendicularScatterOffset(
                    p,
                    idx,
                    perpendicularScatterPx,
                );
                const angleDeg = getPlacementRotationDegrees({
                    rotationMode,
                    baseRotationDeg,
                    placement: p,
                    placementIndex: idx,
                    polarCenter: polarRotationCenter,
                    objectDimensions: objectSvg.dimensions,
                    placementScale,
                    anchor,
                    scatter,
                });

                // Calculate fill color if enabled
                if (gradientEnabled) {
                    if (gradientMode === "random-per-instance") {
                        const fillColor = randomHexColorForPlacement(
                            p.pathIndex,
                            idx,
                        );
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            fillColor,
                        );
                    } else if (
                        gradientMode === "random-palette-per-instance" &&
                        gradientColors.length >= 1
                    ) {
                        const pi = paletteColorIndexForPlacement(
                            p.pathIndex,
                            idx,
                            gradientColors.length,
                        );
                        const fillColor =
                            gradientColors[pi] ?? gradientColors[0];
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            fillColor,
                        );
                    } else if (
                        gradientMode === "random-per-path" &&
                        gradientColors.length >= 1
                    ) {
                        const fillColor = paletteColorForPathIndex(
                            gradientColors,
                            p.pathIndex,
                        );
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            fillColor,
                        );
                    } else if (gradientMode === "source-path-color") {
                        const fillColor = sourcePathColors[p.pathIndex];
                        if (fillColor) {
                            svgContent = applyFillToSvgContent(
                                objectSvg.content,
                                fillColor,
                            );
                        }
                    } else if (
                        (gradientMode === "per-path" ||
                            gradientMode === "global") &&
                        gradientColors.length >= 2
                    ) {
                        const position =
                            gradientMode === "per-path"
                                ? getRepeatedGradientPosition(
                                      p.pathPosition,
                                      gradientPerPathRepeats,
                                  )
                                : p.globalPosition;
                        const gradientColor = getGradientColor(
                            gradientColors,
                            position,
                            maxGradientSteps,
                        );
                        svgContent = applyFillToSvgContent(
                            objectSvg.content,
                            gradientColor,
                        );
                    }
                }

                return `<g transform="translate(${p.x + offsetX + scatter.dx}, ${p.y + offsetY + scatter.dy}) rotate(${angleDeg}, ${-offsetX}, ${-offsetY}) scale(${placementScale})">${svgContent}</g>`;
            })
            .join("\n");

        const pathsSection = showPaths
            ? `  <g class="paths">
${pathContent}
  </g>`
            : "";

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
${pathsSection}
  <g class="objects">
${objectElements}
  </g>
</svg>`;
    };

    const handleDownload = () => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        const objectNames = objectSvgs.map((s) => s.name).join("-");
        link.download = `${objectNames || "object"}-along-${
            pathSvg?.name || "path"
        }.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            distance,
            scale,
            scaleRandomness,
            distanceOffsetRandomness,
            perpendicularScatterPx,
            rotationMode,
            baseRotationDeg,
            polarRotationCenterXPct,
            polarRotationCenterYPct,
            showPaths,
            gridEnabled,
            showGrid,
            gridSize,
            anchor,
            gradientEnabled,
            gradientMode,
            gradientColors,
            sourcePathColors,
            maxGradientSteps,
            gradientPerPathRepeats,
            objectPlacementMode,
            widthProfileEnabled,
            widthProfile,
            widthProfileMode,
            widthProfileMinScale,
            centerSizeFalloffEnabled,
            centerSizeFalloffEdgeScale,
            mergeUnionOnExport,
        }),
        [
            distance,
            scale,
            scaleRandomness,
            distanceOffsetRandomness,
            perpendicularScatterPx,
            rotationMode,
            baseRotationDeg,
            polarRotationCenterXPct,
            polarRotationCenterYPct,
            showPaths,
            gridEnabled,
            showGrid,
            gridSize,
            anchor,
            gradientEnabled,
            gradientMode,
            gradientColors,
            maxGradientSteps,
            gradientPerPathRepeats,
            objectPlacementMode,
            widthProfileEnabled,
            widthProfile,
            widthProfileMode,
            widthProfileMinScale,
            centerSizeFalloffEnabled,
            centerSizeFalloffEdgeScale,
            mergeUnionOnExport,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.distance != null) setDistance(values.distance as number);
        if (values.scale != null) setScale(values.scale as number);
        if (values.scaleRandomness != null)
            setScaleRandomness(values.scaleRandomness as number);
        if (values.distanceOffsetRandomness != null)
            setDistanceOffsetRandomness(
                values.distanceOffsetRandomness as number,
            );
        if (values.perpendicularScatterPx != null)
            setPerpendicularScatterPx(values.perpendicularScatterPx as number);
        if (values.rotationMode != null)
            setRotationMode(
                values.rotationMode as
                    | "align"
                    | "random"
                    | "random-90"
                    | "none"
                    | "polar"
                    | "polar-inverted",
            );
        if (values.baseRotationDeg != null)
            setBaseRotationDeg(values.baseRotationDeg as number);
        if (values.polarRotationCenterXPct != null)
            setPolarRotationCenterXPct(
                values.polarRotationCenterXPct as number,
            );
        if (values.polarRotationCenterYPct != null)
            setPolarRotationCenterYPct(
                values.polarRotationCenterYPct as number,
            );
        if (values.showPaths != null) setShowPaths(values.showPaths as boolean);
        if (values.gridEnabled != null)
            setGridEnabled(values.gridEnabled as boolean);
        if (values.showGrid != null) setShowGrid(values.showGrid as boolean);
        if (values.gridSize != null) setGridSize(values.gridSize as number);
        if (values.anchor != null) setAnchor(values.anchor as string);
        if (values.gradientEnabled != null)
            setGradientEnabled(values.gradientEnabled as boolean);
        if (values.gradientMode != null)
            setGradientMode(values.gradientMode as PathPopulateGradientMode);
        if (values.gradientColors != null)
            setGradientColors(values.gradientColors as string[]);
        if (values.maxGradientSteps != null)
            setMaxGradientSteps(values.maxGradientSteps as number);
        if (values.gradientPerPathRepeats != null)
            setGradientPerPathRepeats(values.gradientPerPathRepeats as number);
        if (values.objectPlacementMode != null)
            setObjectPlacementMode(
                values.objectPlacementMode as "alternating" | "random",
            );
        if (values.widthProfileEnabled != null)
            setWidthProfileEnabled(values.widthProfileEnabled as boolean);
        if (values.widthProfile != null)
            setWidthProfile(values.widthProfile as WidthProfilePreset);
        if (values.widthProfileMode != null)
            setWidthProfileMode(
                values.widthProfileMode as "per-path" | "global",
            );
        if (values.widthProfileMinScale != null)
            setWidthProfileMinScale(values.widthProfileMinScale as number);
        if (values.centerSizeFalloffEnabled != null)
            setCenterSizeFalloffEnabled(
                values.centerSizeFalloffEnabled as boolean,
            );
        if (values.centerSizeFalloffEdgeScale != null)
            setCenterSizeFalloffEdgeScale(
                values.centerSizeFalloffEdgeScale as number,
            );
        if (values.mergeUnionOnExport != null)
            setMergeUnionOnExport(values.mergeUnionOnExport as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "object-along-path",
        defaultPreset: {
            distance: initialDistance,
            scale: initialScale,
            scaleRandomness: 0,
            distanceOffsetRandomness: 0,
            perpendicularScatterPx: 0,
            rotationMode: "align",
            baseRotationDeg: 0,
            polarRotationCenterXPct: 50,
            polarRotationCenterYPct: 50,
            showPaths: true,
            gridEnabled: false,
            showGrid: false,
            gridSize: 10,
            anchor: "center",
            gradientEnabled: true,
            gradientMode: "random-palette-per-instance",
            gradientColors: [
                "#285cd7",
                "#ff8fea",
                "#ff5252",
                "#000000",
                "#52e075",
            ],
            maxGradientSteps: 10,
            gradientPerPathRepeats: 1,
            objectPlacementMode: "alternating",
            widthProfileEnabled: false,
            widthProfile: "taper-both",
            widthProfileMode: "per-path",
            widthProfileMinScale: 0.1,
            centerSizeFalloffEnabled: false,
            centerSizeFalloffEdgeScale: 0.5,
            mergeUnionOnExport: false,
        },
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "object-along-path",
        {
            onPath: (svg) => {
                setError(null);
                setPathSvg(svg);
            },
            onObject: (svg) => {
                setError(null);
                setObjectSvgs([svg]);
            },
        },
        { setError },
    );

    const value: PathPopulateState = {
        objectSvgs,
        pathSvg,
        distance,
        scale,
        scaleRandomness,
        distanceOffsetRandomness,
        perpendicularScatterPx,
        error,
        rotationMode,
        baseRotationDeg,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        showPaths,
        gridEnabled,
        showGrid,
        gridSize,
        anchor,
        gradientEnabled,
        gradientMode,
        gradientColors,
        sourcePathColors,
        maxGradientSteps,
        gradientPerPathRepeats,
        objectPlacementMode,
        widthProfileEnabled,
        widthProfile,
        widthProfileMode,
        widthProfileMinScale,
        centerSizeFalloffEnabled,
        centerSizeFalloffEdgeScale,
        mergeUnionOnExport,
        setMergeUnionOnExport,
        setObjectSvgs,
        addObjectSvg,
        removeObjectSvg,
        setPathSvg,
        setObjectPlacementMode,
        setDistance,
        setScale,
        setScaleRandomness,
        setDistanceOffsetRandomness,
        setPerpendicularScatterPx,
        setError,
        setRotationMode,
        setBaseRotationDeg,
        setPolarRotationCenterXPct,
        setPolarRotationCenterYPct,
        setShowPaths,
        setGridEnabled,
        setShowGrid,
        setGridSize,
        setAnchor,
        setGradientEnabled,
        setGradientMode,
        setGradientColors,
        setMaxGradientSteps,
        setGradientPerPathRepeats,
        setWidthProfileEnabled,
        setWidthProfile,
        setWidthProfileMode,
        setWidthProfileMinScale,
        setCenterSizeFalloffEnabled,
        setCenterSizeFalloffEdgeScale,
        objectInputRefs,
        pathInputRef,
        handleFileLoad,
        pathSegmentCount,
        estimatedPathLengthPx,
        pathViewBox,
        placements,
        estimatedPlacementCount,
        hasGeneratedPlacementsForCurrentParams,
        generatePlacements,
        pathLengthComputePending,
        placementComputePending,
        placementComputeProgress,
        cancelPlacementCompute,
        objectInnerSvgs,
        scaleFactor,
        viewBoxAttr,
        canExport,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <PathPopulateContext.Provider value={value}>
            {children}
        </PathPopulateContext.Provider>
    );
}
